import React from 'react';
import { AUDIT, INTERNAL_USERS, PLANS, STORES } from './data.jsx';
import { isRealTenantsMode, tenantService } from './services.jsx';
// TezusContext — estado global mutável da plataforma
const TezusContext = React.createContext(null);

const TezusProvider = ({ children }) => {
  const useRealStores = typeof isRealTenantsMode === 'function' && isRealTenantsMode();

  const [stores, setStores] = React.useState(() => {
    if (useRealStores) return [];
    return STORES?.length ? [...STORES] : [];
  });
  const [storesLoading, setStoresLoading] = React.useState(useRealStores);
  const [storesError, setStoresError] = React.useState(null);

  const [plans, setPlans] = React.useState(() => PLANS ? [...PLANS] : []);
  const [internalUsers, setInternalUsers] = React.useState(() => INTERNAL_USERS ? [...INTERNAL_USERS] : []);
  const [auditLog, setAuditLog] = React.useState(() => AUDIT ? [...AUDIT] : []);

  const loadRealStores = React.useCallback(async () => {
    if (!useRealStores || !tenantService?.list) return;
    setStoresLoading(true);
    setStoresError(null);
    try {
      const r = await tenantService.list({ page: 1, page_size: 200 });
      setStores(r.data || []);
    } catch (e) {
      setStores([]);
      setStoresError(e?.message || 'Não foi possível carregar lojas da API.');
    } finally {
      setStoresLoading(false);
    }
  }, [useRealStores]);

  React.useEffect(() => {
    if (useRealStores) {
      loadRealStores();
      return;
    }
    if (STORES?.length) setStores([...STORES]);
    if (PLANS?.length) setPlans([...PLANS]);
    if (INTERNAL_USERS?.length) setInternalUsers([...INTERNAL_USERS]);
    if (AUDIT?.length) setAuditLog([...AUDIT]);
  }, [useRealStores, loadRealStores]);

  const logAction = React.useCallback(({ acao, loja, lojaId, kind = 'info' }) => {
    if (useRealStores) return;
    const now = new Date();
    const timeLabel = 'Hoje, ' + now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    setAuditLog(prev => [{
      id: Date.now(),
      user: 'Ana Vargas',
      action: acao,
      storeName: loja || '—',
      storeId: lojaId || null,
      when: timeLabel,
      timestamp: now,
      ip: '177.142.12.8',
      kind,
    }, ...prev]);
  }, [useRealStores]);

  const updateStore = React.useCallback((storeId, updates) => {
    setStores(prev => prev.map(s => s.id === storeId ? { ...s, ...updates } : s));
  }, []);

  const addStore = React.useCallback((store) => {
    setStores(prev => [store, ...prev]);
  }, []);

  const value = React.useMemo(() => ({
    stores, setStores, updateStore, addStore,
    storesLoading, storesError, refetchStores: loadRealStores,
    plans, setPlans,
    internalUsers, setInternalUsers,
    auditLog, setAuditLog,
    logAction,
  }), [
    stores,
    updateStore,
    addStore,
    storesLoading,
    storesError,
    loadRealStores,
    plans,
    internalUsers,
    auditLog,
    logAction,
  ]);

  return (
    <TezusContext.Provider value={value}>
      {children}
    </TezusContext.Provider>
  );
};

export {
  TezusContext,
  TezusProvider,
};
